import { useToggle } from '@react-hookz/web/useToggle/index.js';
import dynamic from 'next/dynamic';

import { useMapBreakpoints } from '#components/Article/Sections/useMapBreakpoints';
import { AspectRatio } from '#components/AspectRatio';
import { PrivacyEmbedConsent } from '#components/PrivacyEmbedConsent';
import { usePrivacyEmbedConsentService } from '#components/PrivacyEmbedConsentTextFetcherHook';
import type { ArticleGeolocationData } from '#shared/types/article';

import styles from './ArticleSectionMap.module.scss';

const ArticleSectionMapDynamic = dynamic(
	() => import('#components/Article/Sections/ArticleSectionMapDynamic').then(({ ArticleSectionMapDynamic }) => ArticleSectionMapDynamic),
	{
		ssr: false,
	}
);

const ArticleSectionMapStatic = dynamic(
	() => import('#components/Article/Sections/ArticleSectionMapStatic').then(({ ArticleSectionMapStatic }) => ArticleSectionMapStatic),
	{
		ssr: false,
	}
);

type Props = {
	geolocation: NonNullable<ArticleGeolocationData['geolocation']>;
};

export function ArticleSectionMap({ geolocation }: Props) {
	const [shouldRenderDynamicMap, toggleShouldRenderDynamicMap] = useToggle(false);
	const breakpoint = useMapBreakpoints();
	const privacy = usePrivacyEmbedConsentService('googlemaps');

	if (!breakpoint || !(privacy.text && privacy.cmpServiceId)) {
		return null;
	}

	return (
		<PrivacyEmbedConsent
			service="googlemaps"
			cmpId={privacy.cmpServiceId}
			text={privacy.text}
			title="Google Maps"
			className={styles.wrapper}
		>
			<AspectRatio as="figure" ratio={breakpoint.ratio} className={styles.wrapper}>
				{shouldRenderDynamicMap ? (
					<ArticleSectionMapDynamic center={geolocation.center} currentBreakpoint={breakpoint.currentBreakpoint} />
				) : (
					<ArticleSectionMapStatic
						onClick={toggleShouldRenderDynamicMap}
						geolocation={geolocation}
						currentBreakpoint={breakpoint.currentBreakpoint}
					/>
				)}
			</AspectRatio>
		</PrivacyEmbedConsent>
	);
}
